/**
 * The stand-in server for the client app's Messages (a client talking to
 * the salons they book with), until the backend lands.
 *
 * Same contract as the salon adapter (api.js) for chat, plus what a client
 * does with a salon's cards: pick one of the offered times, pay a deposit,
 * fill in a form, and open a chat with a salon from a booking. The salon on
 * the other end reads, replies and confirms, like mockAdapter's team does.
 *
 * Not for production: clientApi.js only picks this while MESSAGES_VISIBLE
 * is on a non-live environment. It is separate from the salon's stand-in,
 * so the two sides of one chat don't sync until the real backend joins them.
 */
import { localId, toggleReaction, firstName, firstUrl, linkPreview } from './model';
import { placeholderPhoto } from './mockAdapter';
import { SALONS, SALON_STAFF, seedSalons } from './seedSalons';

const STORE_VERSION = 1;
const MAX_PER_CONVERSATION = 300;

const ANSWERS = [
    'Good question! Let me check and come back to you',
    'Hi {name}, thanks for asking. Give me a moment to check',
];

const REPLIES = [
    'Thanks {name}! Let me check and come back to you',
    'Lovely, thank you {name} 😊',
    'Of course, no problem at all',
    'Perfect, see you soon!',
];

function identity() {
    let user = {};
    try { user = JSON.parse(localStorage.getItem('user')) || {}; } catch (e) { user = {}; }
    const info = user.userInfo || user;
    const id = String(localStorage.getItem('userId') || info._id || 'me');
    const name = [info.first_name || info.firstName, info.last_name || info.lastName].filter(Boolean).join(' ') || info.name || 'You';
    return { id, name };
}

const storageKey = (me) => `bm:client:v${STORE_VERSION}:${me.id}`;

function load(me) {
    try {
        const data = JSON.parse(localStorage.getItem(storageKey(me)));
        return data && data.conversations && data.messages ? data : null;
    } catch (e) { return null; }
}

function seed(me) {
    const people = { [me.id]: { id: me.id, name: me.name, kind: 'client', role: 'You' } };
    SALON_STAFF.forEach((p) => { people[p.id] = { ...p, kind: 'staff' }; });
    const { conversations, messages } = seedSalons(me.id);
    Object.keys(messages).forEach((cid) => {
        messages[cid] = messages[cid].map((msg) => (msg.attachments.some((a) => a.demoTone) ? {
            ...msg, attachments: msg.attachments.map((a) => (a.demoTone ? { ...a, url: placeholderPhoto(a.title || 'Photo', a.demoTone[0], a.demoTone[1]) } : a)),
        } : msg));
    });
    return { people, salons: { ...SALONS }, conversations, messages };
}

export function createClientMockAdapter(options = {}) {
    const me = options.identity || identity();
    const delay = options.delay == null ? 1 : options.delay; // 0 in tests
    let db = (!options.fresh && load(me)) || seed(me);
    db.people[me.id] = { ...(db.people[me.id] || {}), id: me.id, name: me.name, kind: 'client', role: 'You' };
    const listeners = new Set();
    const timers = new Set();

    const emit = (event) => listeners.forEach((fn) => { try { fn(event); } catch (e) { /* one broken listener never stops the others */ } });
    const later = (ms, fn) => {
        const t = setTimeout(() => { timers.delete(t); fn(); }, ms * delay);
        timers.add(t);
    };
    const save = () => {
        try {
            const trimmed = { ...db, messages: {} };
            Object.keys(db.messages).forEach((id) => {
                trimmed.messages[id] = db.messages[id].slice(-MAX_PER_CONVERSATION).map((m) => (m.attachments && m.attachments.some((a) => a.url && a.url.indexOf('blob:') === 0)
                    ? { ...m, attachments: m.attachments.map((a) => (a.url && a.url.indexOf('blob:') === 0 ? { ...a, url: '', expired: true } : a)) } : m));
            });
            localStorage.setItem(storageKey(me), JSON.stringify(trimmed));
        } catch (e) { /* storage full or blocked: the session still works */ }
    };
    const findMessage = (cid, id) => (db.messages[cid] || []).find((x) => x.id === id);
    const setConversation = (c) => { db.conversations[c.id] = c; save(); emit({ type: 'conversation', conversation: c }); };
    const append = (message) => {
        db.messages[message.conversationId] = (db.messages[message.conversationId] || []).concat(message);
        if (!message.system) setConversation({ ...db.conversations[message.conversationId], lastMessageAt: message.createdAt });
        save();
        emit({ type: 'message', message });
    };
    const replace = (next) => {
        db.messages[next.conversationId] = db.messages[next.conversationId].map((x) => (x.id === next.id ? next : x));
        save();
        emit({ type: 'message-updated', message: next });
    };
    const line = (cid, text) => append({ id: localId('srv'), conversationId: cid, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [], system: { text } });
    const staffOf = (c) => c.memberIds.find((id) => id !== me.id);

    /** The salon reads it, then (sometimes) types a reply. */
    function salonAnswers(c, text, { always, asked } = {}) {
        const staff = staffOf(c);
        later(1500, () => {
            const at = new Date().toISOString();
            setConversation({ ...db.conversations[c.id], readUpTo: { ...db.conversations[c.id].readUpTo, [staff]: at } });
            emit({ type: 'read', conversationId: c.id, userId: staff, at });
        });
        if (!always && !asked && Math.random() > 0.65) return; // a question always gets an answer
        later(3200, () => emit({ type: 'typing', conversationId: c.id, userId: staff, on: true }));
        later(5600, () => {
            emit({ type: 'typing', conversationId: c.id, userId: staff, on: false });
            const pool = asked ? ANSWERS : REPLIES;
            const reply = text || pool[Math.floor(Math.random() * pool.length)].replace('{name}', firstName(me.name));
            append({ id: localId('srv'), conversationId: c.id, authorId: staff, createdAt: new Date().toISOString(), text: reply, attachments: [], reactions: {}, mentions: [] });
        });
    }

    const api = {
        me: () => ({ ...me }),

        async bootstrap() {
            return JSON.parse(JSON.stringify({ me: { ...me }, people: db.people, salons: db.salons, conversations: db.conversations, messages: db.messages }));
        },

        async send(conversationId, draft) {
            const c = db.conversations[conversationId];
            if (!c) throw new Error('No such conversation');
            const message = {
                id: localId('srv'), conversationId, authorId: me.id, createdAt: new Date().toISOString(),
                text: draft.text || '', attachments: draft.attachments || [], card: null,
                replyToId: draft.replyToId || null, threadRootId: null, reactions: {}, mentions: [], status: 'sent',
                preview: !(draft.attachments && draft.attachments.length) && firstUrl(draft.text) ? linkPreview(firstUrl(draft.text)) || undefined : undefined,
            };
            db.messages[conversationId] = (db.messages[conversationId] || []).concat(message);
            db.conversations[conversationId] = { ...c, lastMessageAt: message.createdAt, readUpTo: { ...c.readUpTo, [me.id]: message.createdAt } };
            save();
            salonAnswers(c, null, { asked: /\?\s*$/.test(message.text) });
            return JSON.parse(JSON.stringify(message));
        },

        async react(conversationId, messageId, emoji) {
            const current = findMessage(conversationId, messageId);
            if (!current) throw new Error('No such message');
            const next = { ...current, reactions: toggleReaction(current.reactions, emoji, me.id) };
            replace(next);
            return next;
        },

        async edit(conversationId, messageId, text) {
            const current = findMessage(conversationId, messageId);
            if (!current || current.authorId !== me.id) throw new Error('Not yours');
            const next = { ...current, text, editedAt: new Date().toISOString() };
            replace(next);
            return next;
        },

        async remove(conversationId, messageId) {
            const current = findMessage(conversationId, messageId);
            if (!current || current.authorId !== me.id) throw new Error('Not yours');
            const next = { ...current, text: '', attachments: [], deletedAt: new Date().toISOString() };
            replace(next);
            return next;
        },

        async markRead(conversationId, at) {
            const c = db.conversations[conversationId];
            if (!c) return;
            const mark = at || new Date().toISOString();
            if (c.readUpTo[me.id] && c.readUpTo[me.id] >= mark) return;
            db.conversations[conversationId] = { ...c, readUpTo: { ...c.readUpTo, [me.id]: mark } };
            save();
        },

        async setTyping() { /* the stand-in salon doesn't watch */ },

        /** One of the salon's offered times: it's booked. */
        async pickSlot(conversationId, messageId, when) {
            const m = findMessage(conversationId, messageId);
            if (!m || !m.card || m.card.type !== 'slots' || m.card.picked) return null;
            replace({ ...m, card: { ...m.card, picked: when } });
            setConversation({ ...db.conversations[conversationId], nextVisit: when });
            line(conversationId, `You booked ${when}`);
            salonAnswers(db.conversations[conversationId], "Lovely, you're booked in. See you then!", { always: true });
            return null;
        },

        /** Pay the deposit (the real backend takes it through the salon's card payments). */
        async payDeposit(conversationId, messageId) {
            const m = findMessage(conversationId, messageId);
            if (!m || !m.card || m.card.type !== 'deposit' || m.card.status === 'paid') return null;
            replace({ ...m, card: { ...m.card, status: 'paid', paidAt: new Date().toISOString(), cardEnding: '4417' } });
            line(conversationId, `You paid the £${m.card.amount} deposit`);
            return null;
        },

        /** Answers go to the salon and onto the client's record there. */
        async submitForm(conversationId, messageId, answers) {
            const m = findMessage(conversationId, messageId);
            if (!m || !m.card || m.card.type !== 'form') return null;
            replace({ ...m, card: { ...m.card, status: 'completed', completedAt: new Date().toISOString(), answers } });
            line(conversationId, `You sent the ${m.card.name}`);
            salonAnswers(db.conversations[conversationId], 'Thank you, got your form 🙏', { always: true });
            return null;
        },

        async startedForm(conversationId, messageId) {
            const m = findMessage(conversationId, messageId);
            if (m && m.card && m.card.status === 'sent') replace({ ...m, card: { ...m.card, status: 'opened' } });
        },

        /** From a booking: the chat with that salon, started if there isn't one. */
        async openSalon(salon) {
            const existing = Object.keys(db.conversations).map((k) => db.conversations[k]).find((c) => c.salonId === salon.id);
            if (existing) return existing;
            const staffId = `st-${salon.id}`;
            db.salons[salon.id] = { id: salon.id, name: salon.name, mark: (salon.name || 'S').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase(), colour: '#29282D', replies: 'Usually replies within a few hours', area: '' };
            db.people[staffId] = { id: staffId, name: salon.name, salonId: salon.id, kind: 'salon', role: 'Salon' };
            const at = new Date().toISOString();
            const id = localId('c');
            db.conversations[id] = { id, space: 'salons', kind: 'salon', salonId: salon.id, memberIds: [me.id, staffId], pinned: false, muted: false, createdAt: at, lastMessageAt: at, readUpTo: { [me.id]: at } };
            db.messages[id] = [];
            save();
            emit({ type: 'salons', salons: { ...db.salons } });
            emit({ type: 'people', people: { ...db.people } });
            emit({ type: 'conversation', conversation: db.conversations[id] });
            return db.conversations[id];
        },

        subscribe(fn) {
            listeners.add(fn);
            return () => listeners.delete(fn);
        },

        dispose() {
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
            listeners.clear();
        },

        reset() {
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
            db = seed(me);
            save();
            emit({ type: 'reset' });
        },
    };
    return api;
}
